import React, { useCallback } from 'react';
import { useT } from '../i18n';
import { DESIGNER_STYLES, LIMITS, CURRENCIES, RATE_UNITS } from '../constants/designer';
import LocationPicker from './LocationPicker';
import AvatarPicker from './AvatarPicker';
import './DesignerProfileFields.css';

// ── DESIGNER PROFILE FIELDS ──────────────────────────────────────────────────
// The single editor for a designer's profile, rendered BOTH in registration and
// in Settings. Deliberately one component: the two screens must accept exactly
// the same values, and a duplicated form is how "valid at signup, rejected on
// save" bugs happen.
//
// Fully controlled — the parent owns `value` and validation. This component
// renders and reports changes; it never decides what is valid, so registration
// and Settings can't disagree about that either.
// `roomy` — does the CONTAINER have room for the multi-column grid? Passed by
// the parent because only the parent knows: these fields sit in a ~900px
// settings card and in the registration panel, and a media query cannot tell
// the two apart. See the note above the grid in DesignerProfileFields.css.
export default function DesignerProfileFields({ value, onChange, errors = {}, disabled = false, roomy = false }) {
  const { t } = useT();

  // Patch helper — every field edit goes through here so the parent always
  // receives a complete, self-consistent profile object.
  const set = useCallback((patch) => onChange({ ...value, ...patch }), [value, onChange]);

  // ── styles (multi-select) ────────────────────────────────────────────────
  function toggleStyle(key) {
    const on = value.styles.includes(key);
    if (!on && value.styles.length >= LIMITS.styles.max) return;   // cap silently
    set({ styles: on ? value.styles.filter(s => s !== key) : [...value.styles, key] });
  }

  // ── repeating rows (websites / contacts) ─────────────────────────────────
  const setRow = (key, i, v) => set({ [key]: value[key].map((r, j) => (j === i ? v : r)) });
  const addRow = (key, blank, max) => value[key].length < max && set({ [key]: [...value[key], blank] });
  // Never drop to zero rows — an empty list with only an "add" button reads as
  // broken. Clearing the last row blanks it instead of removing it.
  const delRow = (key, i, blank) => set({
    [key]: value[key].length > 1 ? value[key].filter((_, j) => j !== i) : [blank],
  });

  const err = (k) => (errors[k] ? <span className="dpf-err">{t(errors[k])}</span> : null);

  return (
    <div className={`dpf${roomy ? ' dpf--roomy' : ''}`}>
      {/* Shared with the customer flow — see AvatarPicker. */}
      <AvatarPicker
        value={value.avatarUrl}
        onChange={url => set({ avatarUrl: url })}
        disabled={disabled}
      />

      {/* ── full name (required) ── */}
      <label className="dpf-field dpf-field--name">
        <span className="dpf-label">{t('ds_fullname')} <b className="dpf-req">*</b></span>
        <input
          className={`dpf-input${errors.fullName ? ' dpf-input--err' : ''}`}
          value={value.fullName}
          onChange={e => set({ fullName: e.target.value })}
          maxLength={LIMITS.fullName.max}
          disabled={disabled}
          placeholder={t('ds_fullname_ph')}
        />
        {err('fullName')}
      </label>

      {/* ── experience ── */}
      {/* Sizes live in DesignerProfileFields.css, not inline. The `minWidth:
          320px` that was on this label forced its grid column wide at EVERY
          width, which is what left the name field beside it a sliver with a
          two-line label on the registration step. */}
      <label className="dpf-field dpf-field--narrow dpf-field--exp">
        <span className="dpf-label">
          {t('ds_experience')} <span className="dpf-opt">{t('ds_optional')}</span>
        </span>
        <input
          className={`dpf-input${errors.experienceYears ? ' dpf-input--err' : ''}`}
          type="number" inputMode="numeric"
          min={LIMITS.experience.min} max={LIMITS.experience.max}
          value={value.experienceYears}
          onChange={e => set({ experienceYears: e.target.value })}
          disabled={disabled}
          placeholder="0"
        />
        {err('experienceYears')}
      </label>

      {/* ── rate ──────────────────────────────────────────────────────────────
          Amount, currency and unit, on a row of their own.

          It used to be "hourly rate" and sat in the top row beside the name and
          the experience. Three controls do not fit there, and the rate is no
          longer only hourly: a designer charging per square metre had no way to
          say so, and a number labelled "hourly" was simply wrong for them. */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('ds_rate')} <span className="dpf-opt">{t('ds_optional')}</span>
        </span>
        <div className="dpf-row dpf-rate-row">
          <input
            className={`dpf-input dpf-rate-amount${errors.hourlyRate ? ' dpf-input--err' : ''}`}
            type="number" inputMode="decimal" min={LIMITS.hourlyRate.min} max={LIMITS.hourlyRate.max}
            value={value.hourlyRate}
            onChange={e => set({ hourlyRate: e.target.value })}
            disabled={disabled}
            placeholder={t('ds_rate_ph')}
          />
          {/* A dropdown now rather than free text: the four currencies below
              cover who actually bills here, and typing let people save "amd",
              "Dram" and "֏" for the same thing, which no listing page could
              then sort or compare. */}
          <select
            className="dpf-input dpf-currency"
            value={value.currency || CURRENCIES[0].code}
            onChange={e => set({ currency: e.target.value })}
            disabled={disabled}
            aria-label={t('ds_currency')}
          >
            {CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
            ))}
          </select>
          <select
            className="dpf-input dpf-rate-unit"
            value={value.rateUnit || RATE_UNITS[0].id}
            onChange={e => set({ rateUnit: e.target.value })}
            disabled={disabled}
            aria-label={t('ds_rate_unit')}
          >
            {RATE_UNITS.map(u => (
              <option key={u.id} value={u.id}>{t(`ds_unit_${u.id}`)}</option>
            ))}
          </select>
        </div>
        {errors.hourlyRate && <span className="dpf-err">{t(errors.hourlyRate)}</span>}
      </div>

      {/* ── styles ── */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('ds_styles')} <span className="dpf-opt">{t('ds_optional')}</span>
          <span className="dpf-count">{value.styles.length}/{LIMITS.styles.max}</span>
        </span>
        <div className="dpf-chips">
          {DESIGNER_STYLES.map(key => {
            const on = value.styles.includes(key);
            const full = !on && value.styles.length >= LIMITS.styles.max;
            return (
              <button
                key={key} type="button"
                className={`dpf-chip${on ? ' dpf-chip--on' : ''}`}
                onClick={() => toggleStyle(key)}
                disabled={disabled || full}
                aria-pressed={on}
              >{t(`ds_style_${key}`)}</button>
            );
          })}
        </div>
        {err('styles')}
      </div>

      {/* ── location (Leaflet map + geocoded autocomplete) ── */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('ds_location')} <span className="dpf-opt">{t('ds_optional')}</span>
        </span>
        <LocationPicker
          value={value.location}
          address={value.locationAddress}
          coords={value.locationCoords}
          onChange={patch => set(patch)}
          disabled={disabled}
        />
      </div>

      {/* ── description ── */}
      <label className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('ds_description')} <span className="dpf-opt">{t('ds_optional')}</span>
          <span className="dpf-count">{(value.description || '').length}/{LIMITS.description.max}</span>
        </span>
        <textarea
          className={`dpf-input dpf-textarea${errors.description ? ' dpf-input--err' : ''}`}
          rows={4}
          value={value.description}
          onChange={e => set({ description: e.target.value.slice(0, LIMITS.description.max) })}
          disabled={disabled}
          placeholder={t('ds_description_ph')}
        />
        {err('description')}
      </label>

      {/* ── websites (max 3) ── */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('ds_websites')} <span className="dpf-opt">{t('ds_optional')}</span>
          <span className="dpf-count">{value.websites.filter(Boolean).length}/{LIMITS.websites.max}</span>
        </span>
        {value.websites.map((w, i) => (
          <div className="dpf-row" key={i}>
            <input
              className="dpf-input" value={w} disabled={disabled}
              onChange={e => setRow('websites', i, e.target.value)}
              placeholder="https://studio.com"
            />
            <button type="button" className="dpf-row-del" disabled={disabled}
              onClick={() => delRow('websites', i, '')} aria-label={t('ds_remove')}>✕</button>
          </div>
        ))}
        {value.websites.length < LIMITS.websites.max && (
          <button type="button" className="dpf-link-btn" disabled={disabled}
            onClick={() => addRow('websites', '', LIMITS.websites.max)}>+ {t('ds_add_website')}</button>
        )}
        {err('websites')}
      </div>

      {/* ── contacts (max 5, link or phone) ── */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('ds_contacts')} <span className="dpf-opt">{t('ds_optional')}</span>
          <span className="dpf-count">
            {value.contacts.filter(c => c.value.trim()).length}/{LIMITS.contacts.max}
          </span>
        </span>
        {value.contacts.map((c, i) => (
          <div className="dpf-row" key={i}>
            <select
              className="dpf-select" value={c.type} disabled={disabled}
              onChange={e => setRow('contacts', i, { ...c, type: e.target.value })}
            >
              <option value="link">{t('ds_contact_link')}</option>
              <option value="phone">{t('ds_contact_phone')}</option>
            </select>
            <input
              className="dpf-input" value={c.value} disabled={disabled}
              onChange={e => setRow('contacts', i, { ...c, value: e.target.value })}
              placeholder={c.type === 'phone' ? '+374 00 000000' : 'https://instagram.com/…'}
            />
            <button type="button" className="dpf-row-del" disabled={disabled}
              onClick={() => delRow('contacts', i, { type: 'link', value: '' })}
              aria-label={t('ds_remove')}>✕</button>
          </div>
        ))}
        {value.contacts.length < LIMITS.contacts.max && (
          <button type="button" className="dpf-link-btn" disabled={disabled}
            onClick={() => addRow('contacts', { type: 'link', value: '' }, LIMITS.contacts.max)}>
            + {t('ds_add_contact')}
          </button>
        )}
        {err('contacts')}
      </div>
    </div>
  );
}
